import React, { useState, useEffect } from 'react';
import { apiCall } from '../utils/api';
import { toast } from './ui/sonner';
import { 
  DollarSign, TrendingUp, Clock, Calendar, Download, RefreshCw,
  ArrowUpRight, ArrowDownLeft, CheckCircle, AlertCircle, X, 
  Briefcase, Star, FileText, PieChart
} from 'lucide-react';

export default function EarningsDashboard({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState(30);
  const [earnings, setEarnings] = useState(null);
  const [timesheets, setTimesheets] = useState([]);

  useEffect(() => {
    loadEarningsData();
  }, [timeRange]);

  const loadEarningsData = async () => {
    setLoading(true);
    try {
      const [earningsRes, timesheetsRes] = await Promise.all([
        apiCall(`/api/worker/earnings?days=${timeRange}`),
        apiCall(`/api/worker/timesheets?days=${timeRange}`)
      ]);
      
      setEarnings(earningsRes);
      setTimesheets(timesheetsRes.timesheets || []);
    } catch (error) {
      console.error('Failed to load earnings:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatHours = (minutes) => {
    if (!minutes) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getPaymentStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Paid</span>;
      case 'pending':
        return <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">Pending</span>;
      case 'processing':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Processing</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <DollarSign className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Earnings Dashboard</h2>
                <p className="text-white/80">Track your income and work hours</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(parseInt(e.target.value))}
                className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-xl text-white border border-white/30 focus:outline-none"
              >
                <option value={7} className="text-gray-900">Last 7 days</option>
                <option value={30} className="text-gray-900">Last 30 days</option>
                <option value={90} className="text-gray-900">Last 90 days</option>
                <option value={365} className="text-gray-900">Last year</option>
              </select>
              <button
                onClick={onClose}
                className="p-2.5 hover:bg-white/20 rounded-xl transition"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[70vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-green-600 animate-spin" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-sm text-gray-600">Total Earned</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(earnings?.total_earned)}</p>
                  <p className="text-xs text-gray-500 mt-1">Last {timeRange} days</p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-5 border border-blue-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Clock className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-sm text-gray-600">Hours Worked</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{formatHours(earnings?.total_minutes)}</p>
                  <p className="text-xs text-gray-500 mt-1">{earnings?.shifts_completed || 0} shifts</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-5 border border-purple-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="text-sm text-gray-600">Avg Hourly</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(earnings?.avg_hourly_rate)}</p>
                  <p className="text-xs text-gray-500 mt-1">Per hour</p>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-amber-100 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                    </div>
                    <span className="text-sm text-gray-600">Pending</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(earnings?.pending_amount)}</p>
                  <p className="text-xs text-gray-500 mt-1">Awaiting payment</p>
                </div>
              </div>

              {/* Earnings Breakdown */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Payment History */}
                <div className="bg-white rounded-2xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">Recent Payments</h3>
                    <button className="text-sm text-green-600 hover:text-green-700 font-medium">
                      View All
                    </button>
                  </div>
                  
                  {earnings?.recent_payments?.length > 0 ? (
                    <div className="space-y-3">
                      {earnings.recent_payments.slice(0, 5).map((payment, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <ArrowDownLeft className="w-4 h-4 text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 text-sm">{payment.job_title || 'Job Payment'}</p>
                              <p className="text-xs text-gray-500">{new Date(payment.date).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-green-600">+{formatCurrency(payment.amount)}</p>
                            {getPaymentStatusBadge(payment.status)}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-xl">
                      <DollarSign className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">No payments yet</p>
                    </div>
                  )}
                </div>

                {/* Earnings by Job Type */}
                <div className="bg-white rounded-2xl border border-gray-200 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-900">Earnings by Category</h3>
                  </div>
                  
                  {earnings?.by_category?.length > 0 ? (
                    <div className="space-y-3">
                      {earnings.by_category.map((cat, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${
                              idx === 0 ? 'bg-green-500' : 
                              idx === 1 ? 'bg-blue-500' : 
                              idx === 2 ? 'bg-purple-500' : 'bg-gray-400'
                            }`} />
                            <span className="text-sm text-gray-700">{cat.category}</span>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">{formatCurrency(cat.amount)}</p>
                            <p className="text-xs text-gray-500">{cat.count} jobs</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-xl">
                      <PieChart className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">No data available</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Timesheets */}
              <div className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Timesheets</h3>
                  <button 
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-200 transition"
                    data-testid="export-timesheets-btn"
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                </div>
                
                {timesheets.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Job</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Check In</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Check Out</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Hours</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Amount</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {timesheets.map((sheet, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="py-3 px-4 text-sm text-gray-900">
                              {new Date(sheet.date).toLocaleDateString()}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-900">{sheet.job_title}</td>
                            <td className="py-3 px-4 text-sm text-gray-600">
                              {sheet.check_in ? new Date(sheet.check_in).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-'}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-600">
                              {sheet.check_out ? new Date(sheet.check_out).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : '-'}
                            </td>
                            <td className="py-3 px-4 text-sm text-gray-900">{formatHours(sheet.minutes_worked)}</td>
                            <td className="py-3 px-4 text-sm font-medium text-gray-900">{formatCurrency(sheet.amount)}</td>
                            <td className="py-3 px-4">{getPaymentStatusBadge(sheet.payment_status)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <h4 className="font-semibold text-gray-900 mb-1">No Timesheets</h4>
                    <p className="text-gray-500 text-sm">Complete shifts to see your timesheets here</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
